import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_VIDEO_MIME_TYPES,
  createMediaUploader,
  resolveCanonicalCloudinaryUploadResult,
} from '../../utils/mediaUpload';
import AudioDropRecorder from './AudioDropRecorder';
import { getHitLabApiMessage } from './hitLabUtils';
import {
  VOICE_DROP_MAX_SECONDS,
  VOICE_DROP_MIN_SECONDS,
  VOICE_DROP_UPLOAD_PREFIX,
  getMyVoiceDrop,
  normalizeVoiceDropPayload,
  submitVoiceDrop,
} from './hitLabRadioVoiceDropApi';
import {
  FEEDBACK_REWARD_PASS_REWARD_COPY,
  VOICE_DROP_BONUS_REWARD_COPY,
} from './hitLabRadioFeedbackRewardPassApi';

const AUDIO_MIME_TYPES = new Set([
  ...DEFAULT_VIDEO_MIME_TYPES,
  'audio/aac',
  'audio/mp4',
  'audio/mpeg',
  'audio/mpeg-3',
  'audio/mp3',
  'audio/ogg',
  'audio/wav',
  'audio/webm',
  'video/webm',
]);

const isAudioUploadName = (name = '') =>
  /\.(aac|m4a|mp3|oga|ogg|wav|webm)$/i.test(String(name || ''));

const APPROVED_LIVE_LABEL = 'Approved \u2014 live in JG Radio rotation';
const VOICE_DROP_APPROVED_STORAGE_ERROR =
  'Voice drop upload did not land in approved JG Radio storage. Please try again.';
const VOICE_DROP_SESSION_ERROR =
  'Please sign in again to submit your voice drop.';
const VOICE_DROP_RESOURCE_TYPES = ['video', 'raw'];
const VOICE_DROP_APPROVED_STORAGE_PREFIXES = [
  VOICE_DROP_UPLOAD_PREFIX,
  `Images/${VOICE_DROP_UPLOAD_PREFIX}`,
];

const getCanonicalAudioMimeType = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .split(';')[0]
    .trim();

const getStatusLabel = (status) => {
  if (status === 'submitted') return 'Submitted for review';
  if (status === 'approved') return 'Approved';
  if (status === 'rejected') return 'Not approved';
  return String(status || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const getAudioFileExtension = (mimeType = '') => {
  if (/ogg/i.test(mimeType)) return 'ogg';
  if (/wav/i.test(mimeType)) return 'wav';
  if (/mp4|aac/i.test(mimeType)) return 'm4a';
  if (/mpeg|mp3/i.test(mimeType)) return 'mp3';
  return 'webm';
};

const resolveApprovedVoiceDropUploadStorage = (result = {}) => {
  for (const publicIdPrefix of VOICE_DROP_APPROVED_STORAGE_PREFIXES) {
    const storage = resolveCanonicalCloudinaryUploadResult(result, {
      cloudName: 'joegalaxy',
      publicIdPrefix,
      allowedResourceTypes: VOICE_DROP_RESOURCE_TYPES,
    });
    if (storage) {
      return {
        audioUrl: storage.secureUrl,
        audioStorageKey: storage.publicId,
      };
    }
  }
  return null;
};

const getVoiceDropSubmitErrorMessage = (err) => {
  if (Number(err?.response?.status) === 401) return VOICE_DROP_SESSION_ERROR;
  return (
    err?.response?.data?.message ||
    err?.message ||
    'Voice drop could not be submitted.'
  );
};

export default function HitLabRadioListenerVoiceDropPrompt({
  player,
  pass,
  refreshKey = 0,
}) {
  const isEligiblePass = ['submitted', 'approved'].includes(pass?.status);
  const [voiceDrop, setVoiceDrop] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [listenerScript, setListenerScript] = useState('');
  const [permissionGranted, setPermissionGranted] = useState(false);

  const uploader = useMemo(
    () =>
      createMediaUploader({
        videoMimeTypes: AUDIO_MIME_TYPES,
        maxVideoBytes: 12 * 1024 * 1024,
        maxVideoDurationSec: VOICE_DROP_MAX_SECONDS,
        enableVideoMetaCheck: false,
        enableThumbnails: false,
        enableAdaptiveLadder: false,
        isVideoName: isAudioUploadName,
      }),
    [],
  );

  const loadVoiceDrop = useCallback(async () => {
    if (!player?.token || !isEligiblePass) return;
    setLoading(true);
    setError('');
    try {
      const payload = normalizeVoiceDropPayload(await getMyVoiceDrop({ player }));
      setVoiceDrop(payload.voiceDrop);
    } catch (err) {
      setError(getHitLabApiMessage(err, 'Voice drop status could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [isEligiblePass, player]);

  useEffect(() => {
    void loadVoiceDrop();
  }, [loadVoiceDrop, refreshKey]);

  const submitRecording = async ({ blob, durationSeconds, mimeType }) => {
    if (!blob || busy) return;
    if (!player?.token) {
      setError(VOICE_DROP_SESSION_ERROR);
      return;
    }
    if (!permissionGranted) {
      setError('Voice drop consent is required.');
      return;
    }
    const duration = Math.round(durationSeconds);
    if (duration < VOICE_DROP_MIN_SECONDS || duration > VOICE_DROP_MAX_SECONDS) {
      setError('Voice drops must be 10-20 seconds.');
      return;
    }
    setBusy(true);
    setError('');
    setMessage('');
    setUploadProgress(0);
    try {
      const safeMimeType =
        getCanonicalAudioMimeType(mimeType || blob.type) || 'audio/webm';
      const extension = getAudioFileExtension(safeMimeType);
      const audioFile = new File(
        [blob],
        `jg-radio-listener-voice-drop.${extension}`,
        { type: safeMimeType },
      );
      uploader.validate(audioFile);
      const result = await uploader.upload(audioFile, {
        publicIdPrefix: VOICE_DROP_UPLOAD_PREFIX,
        transform: false,
        returnRaw: true,
        dedupe: false,
        onProgress: setUploadProgress,
      });
      const approvedStorage = resolveApprovedVoiceDropUploadStorage(result);
      if (!approvedStorage) {
        throw new Error(VOICE_DROP_APPROVED_STORAGE_ERROR);
      }
      const payload = normalizeVoiceDropPayload(
        await submitVoiceDrop({
          player,
          voiceDrop: {
            audioUrl: approvedStorage.audioUrl,
            audioStorageKey: approvedStorage.audioStorageKey,
            audioMimeType: safeMimeType,
            durationSeconds: duration,
            fileSizeBytes: audioFile.size,
            consentGranted: true,
            listenerScript,
          },
        }),
      );
      setVoiceDrop(payload.voiceDrop);
      setMessage('Submitted for review');
      setPermissionGranted(false);
    } catch (err) {
      setError(getVoiceDropSubmitErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!isEligiblePass) return null;

  const canRecordNew = !voiceDrop || voiceDrop.status === 'rejected';

  return (
    <section className="hitlab-radio-voice-drop" aria-label="Listener voice drop">
      <div className="hitlab-radio-voice-drop-header">
        <div>
          <p className="hitlab-kicker">OPTIONAL VOICE DROP</p>
          <h3>Go live on JG Radio</h3>
        </div>
        {voiceDrop ? (
          <span className="hitlab-radio-voice-drop-status">
            {getStatusLabel(voiceDrop.status)}
          </span>
        ) : null}
      </div>

      <p>
        Record a 10&ndash;20 second listener drop. If approved, your voice gets
        added to JG Radio live rotation.
      </p>
      <p>
        Your written feedback pass is already submitted. This optional voice drop
        is reviewed separately and does not affect your{' '}
        {FEEDBACK_REWARD_PASS_REWARD_COPY} reward decision.
      </p>
      <p>
        Approved listener voice drops receive an additional{' '}
        {VOICE_DROP_BONUS_REWARD_COPY}.
      </p>
      <p>Say something like:</p>
      <blockquote>
        "Hi, this is [first name] from [city]. I just heard [record / artist /
        JG Radio], and [one honest reaction]. I'm plugged in to Joe Galaxy Radio."
      </blockquote>
      <ul>
        <li>Use first name only.</li>
        <li>No private info.</li>
        <li>No background music or copyrighted audio.</li>
        <li>Keep it clean enough for radio.</li>
        <li>Admin approval is required before your drop goes live.</li>
      </ul>

      {voiceDrop?.audioUrl ? (
        <audio controls preload="metadata" src={voiceDrop.audioUrl} />
      ) : null}

      {canRecordNew ? (
        <div className="hitlab-radio-voice-drop-recorder">
          <label>
            Optional script note
            <textarea
              value={listenerScript}
              maxLength="280"
              onChange={(event) => setListenerScript(event.target.value)}
              placeholder="One honest line you plan to say."
            />
          </label>
          <label className="hitlab-radio-voice-drop-consent">
            <input
              type="checkbox"
              checked={permissionGranted}
              onChange={(event) => setPermissionGranted(event.target.checked)}
            />
            I give JG Radio permission to review this voice drop and, if
            approved, add it to JG Radio live rotation.
          </label>
          <AudioDropRecorder
            eyebrow="Listener voice drop"
            title="Record your voice drop"
            description="Make one clean 10-20 second take, listen back, then send it for review."
            minSeconds={VOICE_DROP_MIN_SECONDS}
            maxSeconds={VOICE_DROP_MAX_SECONDS}
            isSubmitting={busy}
            requireConsent
            hasConsent={permissionGranted}
            previewLabel="Listen to your take"
            submitLabel="Submit voice drop"
            submittingLabel={`Uploading ${uploadProgress}%`}
            ariaLabelPrefix="Listener voice drop"
            className="hitlab-radio-audio-drop-recorder"
            onReset={() => {
              setError('');
              setMessage('');
              setUploadProgress(0);
            }}
            onRecordingReady={() => {
              setError('');
              setMessage('');
            }}
            onSubmit={submitRecording}
          />
        </div>
      ) : (
        <p>
          {voiceDrop.status === 'approved'
            ? APPROVED_LIVE_LABEL
            : voiceDrop.status === 'rejected'
              ? 'Not approved'
              : 'Submitted for review'}
        </p>
      )}

      {loading ? <p role="status">Loading voice drop...</p> : null}
      {message ? <p className="hitlab-radio-voice-drop-message">{message}</p> : null}
      {error ? (
        <p className="hitlab-radio-feedback-pass-error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
